import React, { useState, useRef, useMemo } from 'react';
import { format, parseISO, isToday, isTomorrow, addDays } from 'date-fns';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { X, AlignLeft, Calendar as CalendarIcon, CircleCheck, Circle } from 'lucide-react';

const todayStr = () => format(new Date(), 'yyyy-MM-dd');
const tomorrowStr = () => format(addDays(new Date(), 1), 'yyyy-MM-dd');
const formatDue = (str) => {
  if (!str) return null;
  try {
    const d = parseISO(str);
    if (isToday(d)) return 'Today';
    if (isTomorrow(d)) return 'Tomorrow';
    return format(d, 'EEE, MMM d');
  } catch { return str; }
};

const DateChip = ({ label, active, onClick, testid }) => (
  <button type="button" onClick={onClick} className={`text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${active ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-testid={testid}>
    {label}
  </button>
);

// Shared Google-Tasks-style composer used on both the Tasks page and the dashboard To-Do card.
// Add mode: pass onCreate. Edit mode: pass initial (existing task) + onSave + onCancelEdit.
export const TaskComposer = ({ candidates = [], onCreate, initial = null, onSave, onCancelEdit }) => {
  const editMode = !!initial;
  const [open, setOpen] = useState(editMode);
  const [title, setTitle] = useState(initial?.text || '');
  const [details, setDetails] = useState(initial?.details || '');
  const [dueDate, setDueDate] = useState(initial?.dueDate || null); // 'yyyy-MM-dd' | null
  const [mentions, setMentions] = useState(initial?.mentions || []);
  const [calOpen, setCalOpen] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const titleRef = useRef(null);
  const detailsRef = useRef(null);

  const reset = () => { setTitle(''); setDetails(''); setDueDate(null); setMentions([]); setMentionOpen(false); setMentionQuery(''); setCalOpen(false); };
  const openComposer = () => { setOpen(true); setTimeout(() => titleRef.current?.focus(), 0); };
  const closeComposer = () => { setOpen(false); reset(); };
  const cancel = () => { if (editMode) onCancelEdit?.(); else closeComposer(); };

  const addTask = () => {
    const t = title.trim();
    if (!t) { cancel(); return; }
    const payload = {
      id: initial?.id || Date.now(),
      text: t,
      details: details.trim(),
      dueDate: dueDate || null,
      mentions: mentions.filter((m) => t.includes(`@${m.name}`) || details.includes(`@${m.name}`)),
      completed: initial?.completed || false,
    };
    if (editMode) { onSave?.(payload); return; }
    onCreate?.(payload);
    reset();
    setTimeout(() => titleRef.current?.focus(), 0); // stay open for quick add, like Google Tasks
  };

  const onDetailsChange = (value) => {
    setDetails(value);
    const m = value.match(/@([^@]{0,40})$/);
    if (m) { setMentionQuery(m[1] || ''); setMentionOpen(true); }
    else { setMentionOpen(false); setMentionQuery(''); }
  };
  const pickMention = (c) => {
    setDetails((prev) => prev.replace(/@([^@]{0,40})$/, `@${c.name} `));
    setMentions((prev) => (prev.some((m) => m.applicationId === c.applicationId) ? prev : [...prev, { applicationId: c.applicationId, name: c.name }]));
    setMentionOpen(false); setMentionQuery('');
    setTimeout(() => detailsRef.current?.focus(), 0);
  };
  const filteredCandidates = useMemo(() => {
    if (!mentionOpen) return [];
    const q = mentionQuery.toLowerCase();
    return candidates.filter((c) => (c.name || '').toLowerCase().includes(q)).slice(0, 8);
  }, [mentionOpen, mentionQuery, candidates]);

  const setChipDate = (val) => setDueDate((d) => (d === val ? null : val));
  const customDate = dueDate && dueDate !== todayStr() && dueDate !== tomorrowStr();

  if (!open) {
    return (
      <button type="button" onClick={openComposer} className="w-full flex items-center gap-3 px-2 py-2.5 text-blue-600 hover:bg-slate-50 rounded-lg transition-colors" data-testid="tasks-add-row">
        <CircleCheck className="w-5 h-5" />
        <span className="text-sm font-medium">Add a task</span>
      </button>
    );
  }

  return (
    <div className="bg-slate-50 rounded-xl p-3" data-testid="tasks-composer">
      <div className="flex items-start gap-3">
        <Circle className="w-5 h-5 text-slate-300 flex-shrink-0 mt-1.5" />
        <div className="flex-1 min-w-0">
          <input
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addTask(); if (e.key === 'Escape') cancel(); }}
            placeholder="Title"
            className="w-full bg-transparent text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:outline-none py-1"
            data-testid="task-title-input"
          />

          <div className="relative flex items-center gap-2 mt-1">
            <AlignLeft className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              ref={detailsRef}
              value={details}
              onChange={(e) => onDetailsChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !mentionOpen) addTask(); if (e.key === 'Escape') { if (mentionOpen) setMentionOpen(false); else cancel(); } }}
              placeholder="Details — type @ to tag an applicant"
              className="w-full bg-transparent text-sm text-slate-600 placeholder:text-slate-400 focus:outline-none py-1"
              data-testid="task-details-input"
            />
            {mentionOpen && (
              <div className="absolute left-6 top-8 z-40 w-72 bg-white border border-slate-200 rounded-xl shadow-lg py-1 max-h-60 overflow-y-auto" data-testid="mention-picker">
                <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 px-3 pt-1 pb-1">Tag an applicant</p>
                {candidates.length === 0 ? (
                  <p className="text-xs text-slate-400 px-3 py-2">No applicants in your pipeline yet.</p>
                ) : filteredCandidates.length === 0 ? (
                  <p className="text-xs text-slate-400 px-3 py-2">No applicants match “{mentionQuery}”.</p>
                ) : filteredCandidates.map((c) => (
                  <button type="button" key={c.applicationId} onClick={() => pickMention(c)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-2" data-testid={`mention-option-${c.applicationId}`}>
                    <span className="w-6 h-6 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center text-[11px] font-semibold flex-shrink-0">{(c.name || '?').charAt(0).toUpperCase()}</span>
                    <span className="min-w-0">
                      <span className="block text-sm text-slate-800 truncate">{c.name}</span>
                      {c.role && <span className="block text-[11px] text-slate-400 truncate">{c.role}</span>}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="flex items-center gap-1.5 mt-2 flex-wrap">
            <DateChip label="Today" active={dueDate === todayStr()} onClick={() => setChipDate(todayStr())} testid="task-date-today" />
            <DateChip label="Tomorrow" active={dueDate === tomorrowStr()} onClick={() => setChipDate(tomorrowStr())} testid="task-date-tomorrow" />
            <Popover open={calOpen} onOpenChange={setCalOpen}>
              <PopoverTrigger asChild>
                <button type="button" className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full border ${customDate ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'}`} data-testid="task-date-calendar">
                  <CalendarIcon className="w-3.5 h-3.5" />
                  {customDate ? formatDue(dueDate) : 'Pick date'}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={dueDate ? parseISO(dueDate) : undefined} onSelect={(d) => { if (d) setDueDate(format(d, 'yyyy-MM-dd')); setCalOpen(false); }} initialFocus />
              </PopoverContent>
            </Popover>
            {dueDate && (
              <button type="button" onClick={() => setDueDate(null)} className="text-slate-400 hover:text-red-500 p-1" title="Clear date" data-testid="task-date-clear"><X className="w-3.5 h-3.5" /></button>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end items-center gap-2 mt-3">
        <button type="button" onClick={cancel} className="px-3 py-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="task-cancel-btn">Cancel</button>
        <button type="button" onClick={addTask} disabled={!title.trim()} className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:bg-slate-200 disabled:text-slate-400" data-testid="task-save-btn">{editMode ? 'Save' : 'Add task'}</button>
      </div>
    </div>
  );
};

export default TaskComposer;
